// ============================================================================
// ENUMS
// ============================================================================

export enum ChartScope {
  SingleLoadCase = 'SingleLoadCase',
  MultipleLoadCases = 'MultipleLoadCases',
  Both = 'Both'
}

export enum SafetyFactorParameter {
  BarlowBurst = 'BarlowBurst',
  ApiCollapse = 'ApiCollapse',
  PureAxial = 'PureAxial',
  VonMisesEquivalent = 'VonMisesEquivalent'
}

export enum ConnectionSFParameter {
  Burst = 'Burst',
  Collapse = 'Collapse',
  Axial = 'Axial'
}

export enum BurstParameter {
  BurstLoad = 'BurstLoad',
  BarlowBurstRating = 'BarlowBurstRating',
  Both = 'Both'
}

export enum CollapseParameter {
  CollapseLoad = 'CollapseLoad',
  ApiCollapseRating = 'ApiCollapseRating',
  Both = 'Both'
}

export enum AxialForceParameter {
  AxialLoad = 'AxialLoad',
  AxialRating = 'AxialRating',
  Both = 'Both'
}

export enum ForceDisplacementParameter {
  InternalPressure = 'InternalPressure',
  ExternalPressure = 'ExternalPressure',
  Temperature = 'Temperature',
  TrueAxialForce = 'TrueAxialForce',
  AxialStress = 'AxialStress',
  TotalDisplacement = 'TotalDisplacement',
  ThermalDisplacement = 'ThermalDisplacement',
  PoissonDisplacement = 'PoissonDisplacement',
  BucklingDisplacement = 'BucklingDisplacement',
  HookeDisplacement = 'HookeDisplacement',
  Strain = 'Strain',
  EffectiveForce = 'EffectiveForce',
  CriticalSinusoidalBuckling = 'CriticalSinusoidalBuckling',
  DoglegSeverity = 'DoglegSeverity',
  DoglegDueToBuckling = 'DoglegDueToBuckling',
  TorqueDueToBuckling = 'TorqueDueToBuckling'
}

// ============================================================================
// CHART CONFIGURATION INTERFACES
// ============================================================================

export interface SafetyFactorChartConfig {
  scope: ChartScope;
  parameter?: SafetyFactorParameter;
  parameters?: SafetyFactorParameter[];
  loadCaseNames?: string[];
}

export interface ConnectionSafetyFactorChartConfig {
  scope: ChartScope;
  parameter?: ConnectionSFParameter;
  parameters?: ConnectionSFParameter[];
  loadCaseNames?: string[];
}

export interface BurstChartConfig {
  scope: ChartScope;
  parameter?: BurstParameter;
  parameters?: BurstParameter[];
  loadCaseNames?: string[];
}

export interface CollapseChartConfig {
  scope: ChartScope;
  parameter?: CollapseParameter;
  parameters?: CollapseParameter[];
  loadCaseNames?: string[];
}

export interface AxialForceChartConfig {
  scope: ChartScope;
  parameter?: AxialForceParameter;
  parameters?: AxialForceParameter[];
  loadCaseNames?: string[];
}

export interface ForceDisplacementChartConfig {
  scope: ChartScope;
  parameter?: ForceDisplacementParameter;
  parameters?: ForceDisplacementParameter[];
  loadCaseNames?: string[];
}

// ============================================================================
// REPORT OPTIONS INTERFACE
// ============================================================================

/**
 * Configuration options for what to include in a Perivis Results Report
 * 
 * Note: Only the Design Limits Plot is section-aware (sections represent tubing sections
 * inside a casing). All other chart/table options apply at the tubular-string (tubular-group) level.
 */
export interface PerivisReportOptions {
  // Section-level chart options (only applies to Design Limits Plot)
  includeDesignLimitsPlot?: boolean;

  // Tubular-wide chart options (plots and tables are generated per tubular string)
  includeConnectionSafetyFactorPlot?: boolean;
  includeBarlowBurstPlot?: boolean;
  includeApiCollapsePlot?: boolean;
  includeAxialForcePlot?: boolean;
  includeForceDisplacementPlot?: boolean;
  includePressureTempPlot?: boolean;
  includeMaxAllowableWearPlot?: boolean;
  includeLiftOffPlot?: boolean;
  includeBucklingSummaryPlot?: boolean;
  includePackerEnvelopePlot?: boolean;

  // Tubular-wide table options
  includeConnectionSafetyFactorTable?: boolean;
  includeBarlowBurstTable?: boolean;
  includeApiCollapseTable?: boolean;
  includeAxialForceTable?: boolean;
  includeForceDisplacementTable?: boolean;
  includeMaxAllowableWearTable?: boolean;
  includeExpansionJointTable?: boolean;

  // Load case options (these are presented per-load-case when applicable)
  includeSafetyFactorPlot?: boolean;
  includeSafetyFactorTable?: boolean;
  includeMinSafetyFactorGrid?: boolean;
  includeMinConnectionSFGrid?: boolean;

  // Chart configurations (flexible single/multiple load case)
  safetyFactorChartConfig?: SafetyFactorChartConfig;
  connectionSafetyFactorChartConfig?: ConnectionSafetyFactorChartConfig;
  burstChartConfig?: BurstChartConfig;
  collapseChartConfig?: CollapseChartConfig;
  axialForceChartConfig?: AxialForceChartConfig;
  forceDisplacementChartConfig?: ForceDisplacementChartConfig;

  // Table configurations
  forceDisplacementTableColumns?: ForceDisplacementParameter[];
  /** Filter which load cases appear in all report tables. Empty = include all load cases. */
  tableLoadCaseNames?: string[];
}

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_CHART_CONFIGS = {
  safetyFactor: {
    scope: ChartScope.MultipleLoadCases,
    parameters: [SafetyFactorParameter.VonMisesEquivalent]
  } as SafetyFactorChartConfig,

  connectionSafetyFactor: {
    scope: ChartScope.MultipleLoadCases,
    parameters: [ConnectionSFParameter.Burst]
  } as ConnectionSafetyFactorChartConfig,

  burst: {
    scope: ChartScope.MultipleLoadCases,
    parameters: [BurstParameter.BurstLoad]
  } as BurstChartConfig,

  collapse: {
    scope: ChartScope.MultipleLoadCases,
    parameters: [CollapseParameter.CollapseLoad]
  } as CollapseChartConfig,

  axialForce: {
    scope: ChartScope.MultipleLoadCases,
    parameters: [AxialForceParameter.AxialLoad]
  } as AxialForceChartConfig,

  forceDisplacement: {
    scope: ChartScope.MultipleLoadCases,
    parameters: [ForceDisplacementParameter.InternalPressure]
  } as ForceDisplacementChartConfig
};

/**
 * Default Force Displacement table columns
 * Reduces from 17 total columns to most commonly needed data
 */
export const DEFAULT_FORCE_DISPLACEMENT_TABLE_COLUMNS: ForceDisplacementParameter[] = [
  ForceDisplacementParameter.TrueAxialForce,
  ForceDisplacementParameter.AxialStress,
  ForceDisplacementParameter.TotalDisplacement,
  ForceDisplacementParameter.InternalPressure,
  ForceDisplacementParameter.ExternalPressure,
  ForceDisplacementParameter.Temperature,
  ForceDisplacementParameter.EffectiveForce,
  ForceDisplacementParameter.Strain
];

/**
 * Predefined report configurations
 */
export class PerivisReportPresets {
  /**
   * No selections (used for first-time setup)
   */
  static readonly None: PerivisReportOptions = {
    includeDesignLimitsPlot: false,
    includeConnectionSafetyFactorPlot: false,
    includeBarlowBurstPlot: false,
    includeApiCollapsePlot: false,
    includeAxialForcePlot: false,
    includeForceDisplacementPlot: false,
    includePressureTempPlot: false,
    includeMaxAllowableWearPlot: false,
    includeLiftOffPlot: false,
    includeBucklingSummaryPlot: false,
    includePackerEnvelopePlot: false,
    includeConnectionSafetyFactorTable: false,
    includeBarlowBurstTable: false,
    includeApiCollapseTable: false,
    includeAxialForceTable: false,
    includeForceDisplacementTable: false,
    includeMaxAllowableWearTable: false,
    includeExpansionJointTable: false,
    includeSafetyFactorPlot: false,
    includeSafetyFactorTable: false,
    includeMinSafetyFactorGrid: false,
    includeMinConnectionSFGrid: false,
    forceDisplacementTableColumns: []
  };

  /**
   * Default configuration with all options enabled
   */
  static readonly Default: PerivisReportOptions = {
    includeDesignLimitsPlot: true,
    includeConnectionSafetyFactorPlot: true,
    includeBarlowBurstPlot: true,
    includeApiCollapsePlot: true,
    includeAxialForcePlot: true,
    includeForceDisplacementPlot: true,
    includePressureTempPlot: true,
    includeMaxAllowableWearPlot: true,
    includeLiftOffPlot: true,
    includeBucklingSummaryPlot: true,
    includePackerEnvelopePlot: true,
    includeConnectionSafetyFactorTable: true,
    includeBarlowBurstTable: true,
    includeApiCollapseTable: true,
    includeAxialForceTable: true,
    includeForceDisplacementTable: true,
    includeMaxAllowableWearTable: true,
    includeExpansionJointTable: true,
    includeSafetyFactorPlot: true,
    includeSafetyFactorTable: true,
    includeMinSafetyFactorGrid: true,
    includeMinConnectionSFGrid: true,
    safetyFactorChartConfig: DEFAULT_CHART_CONFIGS.safetyFactor,
    connectionSafetyFactorChartConfig: DEFAULT_CHART_CONFIGS.connectionSafetyFactor,
    burstChartConfig: DEFAULT_CHART_CONFIGS.burst,
    collapseChartConfig: DEFAULT_CHART_CONFIGS.collapse,
    axialForceChartConfig: DEFAULT_CHART_CONFIGS.axialForce,
    forceDisplacementChartConfig: DEFAULT_CHART_CONFIGS.forceDisplacement,
    forceDisplacementTableColumns: DEFAULT_FORCE_DISPLACEMENT_TABLE_COLUMNS
  };

  /**
   * Minimal configuration with only essential charts
   */
  static readonly Minimal: PerivisReportOptions = {
    includeDesignLimitsPlot: true,
    includeConnectionSafetyFactorPlot: false,
    includeBarlowBurstPlot: false,
    includeApiCollapsePlot: false,
    includeAxialForcePlot: false,
    includeForceDisplacementPlot: false,
    includePressureTempPlot: false,
    includeMaxAllowableWearPlot: false,
    includeLiftOffPlot: false,
    includeBucklingSummaryPlot: false,
    includePackerEnvelopePlot: false,
    includeConnectionSafetyFactorTable: false,
    includeBarlowBurstTable: false,
    includeApiCollapseTable: false,
    includeAxialForceTable: false,
    includeForceDisplacementTable: false,
    forceDisplacementTableColumns: DEFAULT_FORCE_DISPLACEMENT_TABLE_COLUMNS,
    includeMaxAllowableWearTable: false,
    includeExpansionJointTable: false,
    includeSafetyFactorPlot: true,
    includeSafetyFactorTable: true,
    includeMinSafetyFactorGrid: false,
    includeMinConnectionSFGrid: false
  };

  /**
   * Charts-only configuration (no tables)
   */
  static readonly ChartsOnly: PerivisReportOptions = {
    includeDesignLimitsPlot: true,
    includeConnectionSafetyFactorPlot: true,
    includeBarlowBurstPlot: true,
    includeApiCollapsePlot: true,
    includeAxialForcePlot: true,
    includeForceDisplacementPlot: true,
    includePressureTempPlot: true,
    includeMaxAllowableWearPlot: true,
    includeLiftOffPlot: true,
    includeBucklingSummaryPlot: true,
    includePackerEnvelopePlot: true,
    includeConnectionSafetyFactorTable: false,
    includeBarlowBurstTable: false,
    includeApiCollapseTable: false,
    includeAxialForceTable: false,
    includeForceDisplacementTable: false,
    includeMaxAllowableWearTable: false,
    includeExpansionJointTable: false,
    includeSafetyFactorPlot: true,
    includeSafetyFactorTable: false,
    includeMinSafetyFactorGrid: false,
    includeMinConnectionSFGrid: false,
    safetyFactorChartConfig: DEFAULT_CHART_CONFIGS.safetyFactor,
    connectionSafetyFactorChartConfig: DEFAULT_CHART_CONFIGS.connectionSafetyFactor,
    burstChartConfig: DEFAULT_CHART_CONFIGS.burst,
    collapseChartConfig: DEFAULT_CHART_CONFIGS.collapse,
    axialForceChartConfig: DEFAULT_CHART_CONFIGS.axialForce,
    forceDisplacementChartConfig: DEFAULT_CHART_CONFIGS.forceDisplacement,
    forceDisplacementTableColumns: []
  };

  /**
   * Safety-focused configuration
   */
  static readonly SafetyFocused: PerivisReportOptions = {
    includeDesignLimitsPlot: true,
    includeConnectionSafetyFactorPlot: true,
    includeBarlowBurstPlot: true,
    includeApiCollapsePlot: true,
    includeAxialForcePlot: true,
    includeForceDisplacementPlot: false,
    includePressureTempPlot: false,
    includeMaxAllowableWearPlot: true,
    includeLiftOffPlot: true,
    includeBucklingSummaryPlot: false,
    includePackerEnvelopePlot: false,
    includeConnectionSafetyFactorTable: true,
    includeBarlowBurstTable: false,
    includeApiCollapseTable: false,
    includeAxialForceTable: false,
    includeForceDisplacementTable: false,
    forceDisplacementTableColumns: [],
    includeMaxAllowableWearTable: true,
    includeExpansionJointTable: false,
    includeSafetyFactorPlot: true,
    includeSafetyFactorTable: true,
    includeMinSafetyFactorGrid: true,
    includeMinConnectionSFGrid: true,
    safetyFactorChartConfig: DEFAULT_CHART_CONFIGS.safetyFactor,
    connectionSafetyFactorChartConfig: DEFAULT_CHART_CONFIGS.connectionSafetyFactor,
    burstChartConfig: DEFAULT_CHART_CONFIGS.burst,
    collapseChartConfig: DEFAULT_CHART_CONFIGS.collapse,
    axialForceChartConfig: DEFAULT_CHART_CONFIGS.axialForce,
    forceDisplacementChartConfig: DEFAULT_CHART_CONFIGS.forceDisplacement
  };
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validates SafetyFactorChartConfig
 */
export function validateSafetyFactorConfig(config: SafetyFactorChartConfig): string | null {
  if (!config.scope) {
    return 'Safety Factor chart scope is required';
  }
  
  if ((config.scope === ChartScope.MultipleLoadCases || config.scope === ChartScope.Both) && (!config.parameters || config.parameters.length === 0)) {
    return 'At least one Safety Factor parameter is required for Multiple Load Cases';
  }

  return null;
}

/**
 * Validates ConnectionSafetyFactorChartConfig
 */
export function validateConnectionSafetyFactorConfig(config: ConnectionSafetyFactorChartConfig): string | null {
  if (!config.scope) {
    return 'Connection Safety Factor chart scope is required';
  }

  if ((config.scope === ChartScope.MultipleLoadCases || config.scope === ChartScope.Both) && (!config.parameters || config.parameters.length === 0)) {
    return 'At least one Connection Safety Factor parameter is required for Multiple Load Cases';
  }
  
  return null;
}

/**
 * Validates BurstChartConfig
 */
export function validateBurstConfig(config: BurstChartConfig): string | null {
  if (!config.scope) {
    return 'Burst chart scope is required';
  }
  
  if (config.scope === ChartScope.MultipleLoadCases || config.scope === ChartScope.Both) {
    if (!config.parameters || config.parameters.length === 0) {
      return 'At least one Burst parameter is required for Multiple Load Cases';
    }
    if (config.parameters.includes(BurstParameter.Both)) {
      return 'Burst parameter "Both" is only valid for Single Load Case';
    }
  }
  
  return null;
}

/**
 * Validates CollapseChartConfig
 */
export function validateCollapseConfig(config: CollapseChartConfig): string | null {
  if (!config.scope) {
    return 'Collapse chart scope is required';
  }
  
  if (config.scope === ChartScope.MultipleLoadCases || config.scope === ChartScope.Both) {
    if (!config.parameters || config.parameters.length === 0) {
      return 'At least one Collapse parameter is required for Multiple Load Cases';
    }
    if (config.parameters.includes(CollapseParameter.Both)) {
      return 'Collapse parameter "Both" is only valid for Single Load Case';
    }
  }
  
  return null;
}

/**
 * Validates AxialForceChartConfig
 */
export function validateAxialForceConfig(config: AxialForceChartConfig): string | null {
  if (!config.scope) {
    return 'Axial Force chart scope is required';
  }
  
  if (config.scope === ChartScope.MultipleLoadCases || config.scope === ChartScope.Both) {
    if (!config.parameters || config.parameters.length === 0) {
      return 'At least one Axial Force parameter is required for Multiple Load Cases';
    }
    if (config.parameters.includes(AxialForceParameter.Both)) {
      return 'Axial Force parameter "Both" is only valid for Single Load Case';
    }
  }
  
  return null;
}

/**
 * Validates ForceDisplacementChartConfig
 */
export function validateForceDisplacementConfig(config: ForceDisplacementChartConfig): string | null {
  if (!config.scope) {
    return 'Force Displacement chart scope is required';
  }
  
  if ((config.scope === ChartScope.MultipleLoadCases || config.scope === ChartScope.Both) && (!config.parameters || config.parameters.length === 0)) {
    return 'At least one Force Displacement parameter is required for Multiple Load Cases';
  }
  
  return null;
}

/**
 * Validates entire PerivisReportOptions object
 */
export function validateReportOptions(options: PerivisReportOptions): string[] {
  const errors: string[] = [];
  
  if (options.safetyFactorChartConfig) {
    const error = validateSafetyFactorConfig(options.safetyFactorChartConfig);
    if (error) errors.push(error);
  }
  
  if (options.connectionSafetyFactorChartConfig) {
    const error = validateConnectionSafetyFactorConfig(options.connectionSafetyFactorChartConfig);
    if (error) errors.push(error);
  }
  
  if (options.burstChartConfig) {
    const error = validateBurstConfig(options.burstChartConfig);
    if (error) errors.push(error);
  }
  
  if (options.collapseChartConfig) {
    const error = validateCollapseConfig(options.collapseChartConfig);
    if (error) errors.push(error);
  }
  
  if (options.axialForceChartConfig) {
    const error = validateAxialForceConfig(options.axialForceChartConfig);
    if (error) errors.push(error);
  }
  
  if (options.forceDisplacementChartConfig) {
    const error = validateForceDisplacementConfig(options.forceDisplacementChartConfig);
    if (error) errors.push(error);
  }

  // Validate table configurations
  if (options.includeForceDisplacementTable && (!options.forceDisplacementTableColumns || options.forceDisplacementTableColumns.length === 0)) {
    errors.push('At least one column must be selected for Force Displacement table');
  }
  
  return errors;
}
